import type { StorageBackend, ThinkingLevelSetting } from '../../onboarding/settings.js';
import { loadSettings, saveSettings } from '../../onboarding/settings.js';
import { SettingsComponent } from '../components/settings.js';
import type { IToolExecutionComponent } from '../components/tool-execution-interface.js';
import type { NotificationMode } from '../notify.js';
import { showModalOverlay } from '../overlay.js';
import { handleApiKeysCommand } from './api-keys.js';
import type { SlashCommandContext } from './types.js';

function getCurrentModeColor(ctx: SlashCommandContext): string | undefined {
  return ctx.state.harness.getCurrentMode?.()?.color;
}

function applyQuietModeToRenderedTools(ctx: SlashCommandContext, enabled: boolean, previewLineLimit: number): void {
  const tools = ctx.state.allToolComponents.filter(
    (tool): tool is IToolExecutionComponent => typeof tool.setQuietModeDisplay === 'function',
  );

  tools.forEach(tool => {
    tool.setCompactToolModeColor?.(getCurrentModeColor(ctx));
    tool.setQuietModeDisplay?.(enabled ? 'quiet' : 'normal');
    tool.setQuietPreviewLineLimit?.(previewLineLimit);
  });

  ctx.state.ui.requestRender();
}

export async function handleSettingsCommand(ctx: SlashCommandContext): Promise<void> {
  const state = ctx.state.harness.getState() as any;
  const globalSettings = loadSettings();
  const config = {
    notifications: (state?.notifications ?? 'off') as NotificationMode,
    yolo: state?.yolo === true,
    thinkingLevel: (state?.thinkingLevel ?? 'off') as string,
    currentModelId: ctx.state.harness.getCurrentModelId() ?? '',
    escapeAsCancel: ctx.state.editor.escapeEnabled,
    quietMode: globalSettings.preferences.quietMode,
    quietModeMaxToolPreviewLines: globalSettings.preferences.quietModeMaxToolPreviewLines,
    storageBackend: globalSettings.storage.backend,
    pgConnectionString: globalSettings.storage.pg?.connectionString ?? '',
    libsqlUrl: globalSettings.storage.libsql?.url ?? '',
  };

  return new Promise<void>(resolve => {
    const settings = new SettingsComponent(config, {
      onNotificationsChange: async mode => {
        await ctx.state.harness.setState({ notifications: mode });
        ctx.showInfo(`Notifications: ${mode}`);
      },
      onYoloChange: async enabled => {
        await ctx.state.harness.setState({ yolo: enabled } as any);
      },
      onThinkingLevelChange: async level => {
        await ctx.state.harness.setState({ thinkingLevel: level } as any);
        const current = loadSettings();
        current.preferences.thinkingLevel = level as ThinkingLevelSetting;
        saveSettings(current);
      },
      onEscapeAsCancelChange: async enabled => {
        ctx.state.editor.escapeEnabled = enabled;
        await ctx.state.harness.setState({ escapeAsCancel: enabled });
        await ctx.state.harness.setThreadSetting({ key: 'escapeAsCancel', value: enabled });
      },
      onQuietModeChange: enabled => {
        const current = loadSettings();
        current.preferences.quietMode = enabled;
        current.onboarding.quietModePreferenceSelected = true;
        saveSettings(current);
        ctx.state.quietMode = enabled;
        ctx.state.taskProgress?.setQuietMode(enabled);
        applyQuietModeToRenderedTools(ctx, enabled, ctx.state.quietModeMaxToolPreviewLines);
      },
      onQuietModeMaxToolPreviewLinesChange: lines => {
        const current = loadSettings();
        current.preferences.quietModeMaxToolPreviewLines = lines;
        saveSettings(current);
        ctx.state.quietModeMaxToolPreviewLines = lines;
        applyQuietModeToRenderedTools(ctx, ctx.state.quietMode, lines);
      },
      onStorageBackendChange: (backend: StorageBackend, connectionUrl?: string) => {
        const current = loadSettings();
        current.storage.backend = backend;
        if (backend === 'pg' && connectionUrl !== undefined) {
          current.storage.pg = { ...current.storage.pg, connectionString: connectionUrl };
        } else if (backend === 'libsql') {
          current.storage.libsql = { ...current.storage.libsql, url: connectionUrl || undefined };
        }
        saveSettings(current);
        ctx.state.ui.hideOverlay();
        ctx.stop();
        const label = backend === 'pg' ? 'PostgreSQL' : 'LibSQL';
        console.info(`\nStorage backend changed to ${label}. Restarting is required.\n`);
        process.exit(0);
      },
      onApiKeys: () => {
        ctx.state.ui.hideOverlay();
        resolve();
        handleApiKeysCommand(ctx);
      },
      onClose: () => {
        ctx.state.ui.hideOverlay();
        resolve();
      },
    });

    showModalOverlay(ctx.state.ui, settings, { maxHeight: '75%' });
    settings.focused = true;
  });
}
